/**
 * The hub's top: the period's totals (with change vs the period before) and
 * the "where they came from" mix bars.
 */
import { BadgeDollarSign, CreditCard, Eye, UserPlus, Wallet } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, Delta, Stat, dotClass, num, pct, usd } from "@/features/command/ui";
import type { HubResponse, HubSource } from "./types";

export function TotalsStrip({ data }: { data: HubResponse }) {
  const t = data.totals;
  const p = data.prev_totals;
  const vs = `vs the ${data.days} days before`;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      <Stat label="Visitors" icon={Eye} value={num(t.visitors)} now={t.visitors} prev={p.visitors} sub={`${num(t.visits)} visits`} />
      <Stat
        label="Signups" icon={UserPlus} value={num(t.signups)} now={t.signups} prev={p.signups}
        sub={t.unattributed_signups ? `${num(t.unattributed_signups)} with no source` : vs}
      />
      <Stat label="Paying" icon={CreditCard} value={num(t.paying)} now={t.paying} prev={p.paying} sub="First payment this period" />
      <Stat label="Revenue" icon={BadgeDollarSign} tone="brand" big value={usd(t.revenue)} now={t.revenue} prev={p.revenue} sub={vs} />
      <div className="col-span-2 sm:col-span-1">
        <Stat
          label="Ad spend" icon={Wallet} value={usd(t.spend)}
          sub={<span className="flex items-center gap-1.5">Ads + tool costs <Delta now={t.spend} prev={p.spend} invert /></span>}
        />
      </div>
    </div>
  );
}

/** Two stacked bars: how this period's signups and revenue split by source. */
export function MixCard({ tops, data }: { tops: HubSource[]; data: HubResponse }) {
  const rows: Array<{ key: "signups" | "revenue"; label: string; total: number; extra?: number }> = [
    { key: "signups", label: "Signups", total: data.totals.signups, extra: data.totals.unattributed_signups },
    { key: "revenue", label: "Revenue", total: data.totals.revenue },
  ];
  if (!data.totals.signups && !data.totals.revenue) return null;
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-zinc-100">Where they came from</h2>
        <span className="text-[11px] text-zinc-500">Last {data.days} days</span>
      </div>
      <div className="space-y-4">
        {rows.map((r) => {
          const parts = tops
            .map((s) => ({ s, v: Number(s.n?.[r.key] ?? 0) }))
            .filter((x) => x.v > 0)
            .sort((a, b) => b.v - a.v);
          if (!r.total) {
            return (
              <div key={r.key} className="flex items-center gap-3 text-xs text-zinc-500">
                <span className="w-16 shrink-0 font-medium text-zinc-400">{r.label}</span>
                <span>None this period</span>
              </div>
            );
          }
          const fmt = (v: number) => (r.key === "revenue" ? usd(v) : num(v));
          return (
            <div key={r.key} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <span className="w-16 shrink-0 text-xs font-medium text-zinc-400">{r.label}</span>
              <div className="min-w-0 flex-1">
                <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-zinc-800/80">
                  {parts.map(({ s, v }) => (
                    <div key={s.id} title={`${s.name}: ${fmt(v)}`} className={cn("h-full border-r border-zinc-950/40 last:border-r-0", dotClass(s.color))} style={{ width: `${(v / r.total) * 100}%` }} />
                  ))}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-zinc-400">
                  {parts.map(({ s, v }) => (
                    <span key={s.id} className="flex items-center gap-1.5">
                      <span className={cn("h-1.5 w-1.5 rounded-full", dotClass(s.color))} />
                      {s.name}
                      <span className="tabular-nums text-zinc-500">{fmt(v)} · {pct(v / r.total)}</span>
                    </span>
                  ))}
                  {!!r.extra && (
                    <span className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-zinc-600" />
                      No source <span className="tabular-nums text-zinc-500">{num(r.extra)}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
